// @file Helper functions for color manipulation

interface RGB {
  r: number
  g: number
  b: number
}

interface RGBA extends RGB {
  a: number
}

type RGBString = `rgb(${number}, ${number}, ${number})`
type RGBAString = `rgba(${number}, ${number}, ${number}, ${number})`
type HSLString = `hsl(${number}, ${number}%, ${number}%)`
type HSLAString = `hsla(${number}, ${number}%, ${number}%, ${number})`
type HEXString = `#${string}`
type ColorString = RGBString | RGBAString | HSLString | HSLAString | HEXString
type LinearGradientString = `linear-gradient(${string})`
type CompositeBackgroundString = `${LinearGradientString}, ${ColorString}`

function rgba(r: number, g: number, b: number, a: number): RGBA {
  return { r, g, b, a }
}

/**
 * Calculate ratio based on relative luminance
 * @see https://www.w3.org/TR/WCAG20-TECHS/G17.html
 * @see https://en.wikipedia.org/wiki/Relative_luminance
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/Understanding_Colors_and_Luminance
 * @see https://webaim.org/resources/contrastchecker/ -- tool to calculating ratio for 2 colors
 */
function computeLuminanceRatio(e1: RGB | RGBA, e2: RGB | RGBA): number {
  const l1 = (0.2126 * e1.r) / 255 + (0.7152 * e1.g) / 255 + (0.0722 * e1.b) / 255
  const l2 = (0.2126 * e2.r) / 255 + (0.7152 * e2.g) / 255 + (0.0722 * e2.b) / 255
  const ratio = (l1 + 0.05) / (l2 + 0.05)
  return ratio > 1 ? ratio : 1 / ratio
}

function serializeRgbaStringToRgba(rgbaString: string): RGBA {
  const imageBackgroundArray = rgbaString.replace(/(a|b|g|r|\(|\)|\s)/g, '').split(',')
  return {
    r: parseInt(imageBackgroundArray[0], 10),
    g: parseInt(imageBackgroundArray[1], 10),
    b: parseInt(imageBackgroundArray[2], 10),
    a: imageBackgroundArray.length === 4 ? parseInt(imageBackgroundArray[3], 10) : 1,
  }
}

/**
 * Change color brightness
 * @param color Original color, either a RBG object or a string
 * @param percent > 0 to lighten, < 0 to darken
 * @returns RGB color string
 */
function shadeColor(color: RGB | string, percent: number): string {
  let { r, g, b } = typeof color === 'string' ? serializeRgbaStringToRgba(color) : color

  r = parseInt(String((r * (100 + percent)) / 100))
  g = parseInt(String((g * (100 + percent)) / 100))
  b = parseInt(String((b * (100 + percent)) / 100))

  r = Math.min(r, 255)
  g = Math.min(g, 255)
  b = Math.min(b, 255)

  return `rgb(${r}, ${g}, ${b})`
}

function shadeColorWithOpacity(baseColor: RGB, overlayColor: RGB, opacityPercentage: number): RGB {
  // Convert percentage to a decimal (e.g., 10% becomes 0.1)
  let opacity = opacityPercentage / 100

  // Ensure opacity is between 0 and 1
  opacity = Math.max(0, Math.min(1, opacity))

  // Apply the alpha blending formula
  const shadedColor: RGB = {
    r: Math.round((1 - opacity) * baseColor.r + opacity * overlayColor.r),
    g: Math.round((1 - opacity) * baseColor.g + opacity * overlayColor.g),
    b: Math.round((1 - opacity) * baseColor.b + opacity * overlayColor.b),
  }

  return shadedColor
}

function convertHexToRGB(hex: string): RGB | null {
  // Check for shorthand form (e.g. "#03F") and convert to full form (e.g. "#0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  hex = hex.replace(shorthandRegex, function (_, r: string, g: string, b: string) {
    return r + r + g + g + b + b
  })

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (result !== null) {
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
  }

  return null
}

/**
 * Convert RGB values to HSL
 * @param r Red value (0-255)
 * @param g Green value (0-255)
 * @param b Blue value (0-255)
 * @returns HSL object with h (0-360), s (0-100), l (0-100)
 */
function convertRGBToHSL(rgbColor: RGB | string): { h: number; s: number; l: number } {
  let r: number, g: number, b: number

  if (typeof rgbColor === 'string') {
    const rgbValues = rgbColor.match(/\d+/g)
    if (!rgbValues || rgbValues.length < 3) {
      throw new Error('Invalid RGB string format. Expected "rgb(r,g,b)"')
    }
    r = parseInt(rgbValues[0], 10)
    g = parseInt(rgbValues[1], 10)
    b = parseInt(rgbValues[2], 10)
  } else {
    r = rgbColor.r
    g = rgbColor.g
    b = rgbColor.b
  }

  // Normalize RGB values to 0-1 range
  r /= 255
  g /= 255
  b /= 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  let l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  // Convert to degrees and percentages
  h = Math.round(h * 360)
  s = Math.round(s * 100)
  l = Math.round(l * 100)

  return { h, s, l }
}

function convertRGBToHex(rgb: RGB): string {
  const toHex = (value: number) => {
    const hex = value.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

/**
 * Extract RGB values from CSS color string and return as "r, g, b" format
 * Useful for CSS variables that need to be used with rgba() and custom alpha values
 * @param color CSS color string (hex, rgb(), rgba(), or "r, g, b")
 * @returns RGB values as "r, g, b" string (e.g., "255, 97, 133")
 * @example
 * serializeCssColorToRgbString('rgb(255, 97, 133)') // "255, 97, 133"
 * serializeCssColorToRgbString('#ff6185') // "255, 97, 133"
 * serializeCssColorToRgbString('rgba(255, 97, 133, 0.5)') // "255, 97, 133"
 */
function serializeCssColorToRgbString(color: string): string {
  // Handle rgb() or rgba() format: rgb(255, 97, 133) or rgba(255, 97, 133, 0.5)
  const rgbMatch = color.match(/rgba?\(([^)]+)\)/)
  if (rgbMatch) {
    // Extract just the r, g, b values (ignore alpha if present)
    const values = rgbMatch[1]
      .split(',')
      .slice(0, 3)
      .map((v) => v.trim())
    return values.join(', ')
  }

  // Handle hex format: #ff6185 or #f61
  if (color.startsWith('#')) {
    const rgb = convertHexToRGB(color)
    if (rgb) {
      return `${rgb.r}, ${rgb.g}, ${rgb.b}`
    }
  }

  // Fallback: return as-is (assume it's already in "r, g, b" format)
  return color
}

export {
  computeLuminanceRatio,
  convertHexToRGB,
  convertRGBToHex,
  convertRGBToHSL,
  rgba,
  serializeCssColorToRgbString,
  serializeRgbaStringToRgba,
  shadeColor,
  shadeColorWithOpacity,
}
export type {
  ColorString,
  CompositeBackgroundString,
  HEXString,
  HSLAString,
  HSLString,
  LinearGradientString,
  RGB,
  RGBA,
  RGBAString,
  RGBString,
}
